import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Plug, Plus, Activity, AlertCircle, Cloud, Headphones, MessageSquare,
    Database, Building2, Globe, X, RefreshCw, Copy, Check, CheckCircle2,
    Zap, Trash2, Clock, ArrowRight, Calendar,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { StatCard } from "@/components/ui/StatCard";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import pt from "@/lib/i18n";

const STATUS_VARIANT = { active: "success", inactive: "neutral", error: "error", configuring: "warning" };
const PROVIDER_ICONS = {
    salesforce: Cloud, zendesk: Headphones, whatsapp: MessageSquare,
    oracle_bss: Database, sap: Building2, custom: Globe,
};
const PROVIDER_COLOR = {
    salesforce: "#00A1E0", zendesk: "#03363D", whatsapp: "#25D366",
    oracle_bss: "#F80000", sap: "#0FAAFF", custom: "#7A5A9C",
};

const _fmtRelative = (iso) => {
    if (!iso) return "—";
    const t = new Date(iso);
    const diffMin = Math.round((Date.now() - t.getTime()) / 60000);
    if (diffMin < 1) return "agora mesmo";
    if (diffMin < 60) return `há ${diffMin} min`;
    const hr = Math.round(diffMin / 60);
    if (hr < 24) return `há ${hr}h`;
    return `há ${Math.round(hr / 24)} dias`;
};

const Sparkline = ({ data, color }) => {
    const safe = (data || []).map((v, i) => ({ i, v }));
    return (
        <LineChart width={80} height={28} data={safe} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
            <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.6} dot={false} isAnimationActive={false} />
        </LineChart>
    );
};

const Wizard = ({ onClose, onCreated }) => {
    const [step, setStep] = useState(1);
    const [data, setData] = useState({
        name: "", type: "webhook", provider: "custom",
        description: "", base_url: "", auth_type: "hmac",
    });
    const [mappings, setMappings] = useState([{ source_field: "", target_field: "", transform_rule: "" }]);
    const [created, setCreated] = useState(null);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [secretCopied, setSecretCopied] = useState(false);
    const [loading, setLoading] = useState(false);

    const next = () => setStep((s) => Math.min(s + 1, 5));
    const back = () => setStep((s) => Math.max(s - 1, 1));

    const save = async () => {
        setLoading(true);
        try {
            const r = await apiPost("/integrations/connectors", {
                name: data.name, type: data.type, provider: data.provider,
                description: data.description,
                config: { base_url: data.base_url || undefined, auth_type: data.auth_type, timeout_sec: 30 },
            });
            setCreated(r);
            // Save mappings
            for (const m of mappings) {
                if (m.source_field && m.target_field) {
                    await apiPost(`/integrations/connectors/${r.id}/mappings`, m);
                }
            }
            setStep(4);
        } catch (e) {}
        setLoading(false);
    };

    const testConn = async () => {
        if (!created) return;
        setTesting(true);
        try {
            const r = await apiPost(`/integrations/connectors/${created.id}/test`, { payload: { ping: "pong" } });
            setTestResult(r);
        } catch (e) { setTestResult({ ok: false }); }
        setTesting(false);
    };

    const activate = async () => {
        if (!created) return;
        try {
            await apiPatch(`/integrations/connectors/${created.id}`, { status: "active" });
            onCreated();
            onClose();
        } catch (e) {}
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="wizard-modal"
        >
            <motion.div
                initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                className="bg-[color:var(--bg-surface)] rounded-2xl border-2 border-[color:var(--copper)] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 lg:p-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--copper-dark)]">Passo {step} de 5</p>
                        <h2 className="font-display text-2xl text-[color:var(--slate-deep)] mt-1">{pt.integrations.wizardTitle}</h2>
                    </div>
                    <button onClick={onClose} className="text-[color:var(--slate-mid)] hover:text-[color:var(--slate-deep)]" data-testid="btn-close-wizard"><X size={20} /></button>
                </div>

                {/* Progress */}
                <div className="flex gap-1 mb-6">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className={"flex-1 h-1 rounded-full " + (i <= step ? "bg-[color:var(--copper)]" : "bg-[color:var(--glass-border)]")} />
                    ))}
                </div>

                {step === 1 && (
                    <div className="space-y-3" data-testid="wizard-step-1">
                        <p className="font-body text-xs uppercase tracking-[0.14em] text-[color:var(--slate-mid)] font-mono">{pt.integrations.wizardStep1}</p>
                        <GlassInput
                            placeholder={pt.integrationsExtra?.namePlaceholder || "Nome do conector"} value={data.name}
                            onChange={(e) => setData({ ...data, name: e.target.value })}
                            data-testid="wizard-name"
                        />
                        <textarea
                            rows={2}
                            placeholder={pt.integrationsExtra?.descPlaceholder || "Descrição (opcional)"}
                            value={data.description}
                            onChange={(e) => setData({ ...data, description: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg bg-white/80 border border-[color:var(--glass-border)] text-sm font-body resize-none focus:outline-none focus:border-[color:var(--copper)]"
                            data-testid="wizard-description"
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <select value={data.type} onChange={(e) => setData({ ...data, type: e.target.value })} className="px-3 py-2 rounded-lg bg-white/80 border border-[color:var(--glass-border)] text-sm font-body" data-testid="wizard-type">
                                {Object.entries(pt.integrations.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                            <select value={data.provider} onChange={(e) => setData({ ...data, provider: e.target.value })} className="px-3 py-2 rounded-lg bg-white/80 border border-[color:var(--glass-border)] text-sm font-body" data-testid="wizard-provider">
                                <option value="custom">Personalizado</option>
                                <option value="salesforce">Salesforce</option>
                                <option value="zendesk">Zendesk</option>
                                <option value="whatsapp">WhatsApp</option>
                                <option value="oracle_bss">Oracle BSS</option>
                                <option value="sap">SAP</option>
                            </select>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-3" data-testid="wizard-step-2">
                        <p className="font-body text-xs uppercase tracking-[0.14em] text-[color:var(--slate-mid)] font-mono">{pt.integrations.wizardStep2}</p>
                        <GlassInput
                            placeholder="Base URL (https://api.exemplo.com)"
                            value={data.base_url}
                            onChange={(e) => setData({ ...data, base_url: e.target.value })}
                            data-testid="wizard-base-url"
                        />
                        <select value={data.auth_type} onChange={(e) => setData({ ...data, auth_type: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white/80 border border-[color:var(--glass-border)] text-sm font-body" data-testid="wizard-auth-type">
                            <option value="none">{pt.integrationsExtra?.authNone || "Sem autenticação"}</option>
                            <option value="api_key">Chave API</option>
                            <option value="bearer">Bearer Token</option>
                            <option value="basic">Basic Auth</option>
                            <option value="hmac">HMAC (webhook)</option>
                            <option value="oauth2">OAuth 2.0</option>
                        </select>
                        <p className="font-body text-xs text-[color:var(--slate-mid)] italic">
                            Segredos serão referenciados via vault — nunca armazenamos chaves em texto plano.
                        </p>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-3" data-testid="wizard-step-3">
                        <p className="font-body text-xs uppercase tracking-[0.14em] text-[color:var(--slate-mid)] font-mono">{pt.integrations.wizardStep3}</p>
                        <div className="space-y-2">
                            {mappings.map((m, i) => (
                                <div key={i} className="flex gap-2 items-center">
                                    <input
                                        placeholder={pt.integrations.sourceField}
                                        value={m.source_field}
                                        onChange={(e) => { const nx = [...mappings]; nx[i].source_field = e.target.value; setMappings(nx); }}
                                        className="flex-1 px-2.5 py-1.5 rounded-md bg-white/80 border border-[color:var(--glass-border)] text-sm font-mono"
                                        data-testid={`mapping-src-${i}`}
                                    />
                                    <ArrowRight size={14} className="text-[color:var(--copper)] shrink-0" />
                                    <input
                                        placeholder={pt.integrations.targetField}
                                        value={m.target_field}
                                        onChange={(e) => { const nx = [...mappings]; nx[i].target_field = e.target.value; setMappings(nx); }}
                                        className="flex-1 px-2.5 py-1.5 rounded-md bg-white/80 border border-[color:var(--glass-border)] text-sm font-mono"
                                        data-testid={`mapping-tgt-${i}`}
                                    />
                                    <select
                                        value={m.transform_rule || ""}
                                        onChange={(e) => { const nx = [...mappings]; nx[i].transform_rule = e.target.value; setMappings(nx); }}
                                        className="px-2 py-1.5 rounded-md bg-white/80 border border-[color:var(--glass-border)] text-xs font-body"
                                    >
                                        <option value="">—</option>
                                        <option value="uppercase">UPPER</option>
                                        <option value="lowercase">lower</option>
                                        <option value="trim">trim</option>
                                    </select>
                                    <button onClick={() => setMappings(mappings.filter((_, idx) => idx !== i))} className="text-[color:var(--error)]"><Trash2 size={13} /></button>
                                </div>
                            ))}
                            <button
                                onClick={() => setMappings([...mappings, { source_field: "", target_field: "", transform_rule: "" }])}
                                className="text-[color:var(--copper-dark)] text-xs flex items-center gap-1 hover:text-[color:var(--copper)]"
                                data-testid="btn-add-mapping"
                            >
                                <Plus size={12} /> {pt.integrations.addMapping}
                            </button>
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div className="space-y-3" data-testid="wizard-step-4">
                        <p className="font-body text-xs uppercase tracking-[0.14em] text-[color:var(--slate-mid)] font-mono">{pt.integrations.wizardStep4}</p>
                        {created?.webhook_secret && (
                            <div className="p-4 rounded-lg bg-[color:var(--copper-subtle)] border-2 border-[color:var(--copper)]">
                                <p className="font-body text-xs font-medium text-[color:var(--copper-dark)] mb-2">{pt.integrations.webhookUrl}</p>
                                <p className="font-mono text-xs text-[color:var(--slate-deep)] mb-3 break-all">{created.webhook_url}</p>
                                <p className="font-body text-xs font-medium text-[color:var(--copper-dark)] mb-2">{pt.integrations.webhookSecret}</p>
                                <div className="flex items-center gap-2">
                                    <code className="font-mono text-[11px] text-[color:var(--slate-deep)] bg-white/70 px-2 py-1.5 rounded flex-1 break-all">{created.webhook_secret}</code>
                                    <button
                                        onClick={async () => {
                                            try {
                                                if (navigator.clipboard?.writeText) {
                                                    await navigator.clipboard.writeText(created.webhook_secret);
                                                } else {
                                                    // Fallback: select text via temporary input
                                                    const ta = document.createElement("textarea");
                                                    ta.value = created.webhook_secret;
                                                    ta.style.position = "fixed";
                                                    ta.style.left = "-9999px";
                                                    document.body.appendChild(ta);
                                                    ta.select();
                                                    try { document.execCommand("copy"); } catch (e) {}
                                                    document.body.removeChild(ta);
                                                }
                                                setSecretCopied(true);
                                                setTimeout(() => setSecretCopied(false), 2000);
                                            } catch (err) {
                                                // Clipboard blocked (sandbox/iframe/permissions) — show fallback visual
                                                setSecretCopied(true);
                                                setTimeout(() => setSecretCopied(false), 2000);
                                            }
                                        }}
                                        className="text-[color:var(--copper-dark)] p-2 rounded hover:bg-white/50"
                                        data-testid="btn-copy-secret"
                                    >
                                        {secretCopied ? <Check size={14} className="text-[color:var(--success)]" /> : <Copy size={14} />}
                                    </button>
                                </div>
                                <p className="font-body text-[10px] text-[color:var(--error)] mt-2 italic">⚠️ {pt.integrations.secretWarning}</p>
                            </div>
                        )}
                        <NxButton variant="copper" onClick={testConn} disabled={testing} icon={<Zap size={14} />} data-testid="btn-test-connection" className="w-full">
                            {testing ? "A testar…" : pt.integrations.test}
                        </NxButton>
                        {testResult?.ok && (
                            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex items-center gap-2 text-[color:var(--success)] font-body text-sm" data-testid="test-success">
                                <CheckCircle2 size={18} /> {testResult.message}
                            </motion.div>
                        )}
                    </div>
                )}

                {step === 5 && (
                    <div className="text-center py-6" data-testid="wizard-step-5">
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mx-auto w-16 h-16 rounded-full bg-[color:var(--success)] flex items-center justify-center mb-3">
                            <CheckCircle2 size={32} className="text-white" />
                        </motion.div>
                        <h3 className="font-display text-xl text-[color:var(--slate-deep)] mb-2">Conector pronto!</h3>
                        <p className="font-body text-sm text-[color:var(--slate-mid)] mb-4">O conector "{data.name}" será activado e começará a processar eventos.</p>
                    </div>
                )}

                <div className="flex justify-between mt-6 pt-4 border-t border-[color:var(--glass-border)]">
                    {step > 1 && step < 5 && <NxButton variant="secondary" size="sm" onClick={back} data-testid="btn-wizard-back">{pt.integrations.back}</NxButton>}
                    <div className="flex-1" />
                    {step < 3 && <NxButton variant="copper" size="sm" onClick={next} disabled={!data.name && step === 1} data-testid="btn-wizard-next">{pt.integrations.next}</NxButton>}
                    {step === 3 && <NxButton variant="copper" size="sm" onClick={save} disabled={loading} data-testid="btn-wizard-save">{loading ? "..." : pt.integrations.save}</NxButton>}
                    {step === 4 && testResult?.ok && <NxButton variant="copper" size="sm" onClick={() => setStep(5)} data-testid="btn-wizard-continue">{pt.integrations.next}</NxButton>}
                    {step === 5 && <NxButton variant="copper" size="sm" onClick={activate} data-testid="btn-wizard-activate">{pt.integrationsExtra?.activate || "Activar"}</NxButton>}
                </div>
            </motion.div>
        </motion.div>
    );
};

const IntegrationsPage = () => {
    const [summary, setSummary] = useState(null);
    const [connectors, setConnectors] = useState(null);
    const [events, setEvents] = useState([]);
    const [selectedConnector, setSelectedConnector] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showWizard, setShowWizard] = useState(false);
    const [scheduler, setScheduler] = useState(null);
    const [runningCron, setRunningCron] = useState(false);

    const load = async () => {
        try {
            const [s, list, ev, sched] = await Promise.all([
                apiGet("/integrations/events-summary"),
                apiGet("/integrations/connectors"),
                apiGet("/integrations/events?limit=20"),
                apiGet("/integrations/scheduler/status"),
            ]);
            setSummary(s); setConnectors(list || []);
            setEvents(ev || []); setScheduler(sched);
        } catch (e) {}
    };

    useEffect(() => { load(); }, []);

    const runCron = async () => {
        setRunningCron(true);
        try {
            await apiPost("/integrations/scheduler/run-now/morning-briefing");
            await load();
        } catch (e) {}
        setRunningCron(false);
    };

    const retryEvent = async (id) => {
        try {
            await apiPost(`/integrations/events/${id}/retry`);
            await load();
            if (selectedEvent?.id === id) {
                const e = await apiGet(`/integrations/events/${id}`);
                setSelectedEvent(e);
            }
        } catch (err) {}
    };

    return (
        <div data-testid="integrations-page" className="space-y-6">
            <PageHeader
                title={pt.integrations.title}
                subtitle={pt.integrations.subtitle}
                testId="integrations-header"
                right={<NxButton variant="copper" onClick={() => setShowWizard(true)} icon={<Plus size={14} />} data-testid="btn-new-connector">{pt.integrations.newConnector}</NxButton>}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label={pt.integrations.kpiTotal} value={summary?.total_connectors ?? 0} index={0} testId="kpi-total-connectors" />
                <StatCard label={pt.integrations.kpiActive} value={summary?.active_connectors ?? 0} index={1} testId="kpi-active-connectors" />
                <StatCard label={pt.integrations.kpiEvents24h} value={summary?.events_24h ?? 0} index={2} testId="kpi-events-24h" />
                <StatCard label={pt.integrations.kpiErrorRate} value={summary?.error_rate_pct ?? 0} format="percent" index={3} testId="kpi-error-rate" />
            </div>

            {/* Scheduler info */}
            {scheduler?.jobs?.length > 0 && (
                <GlassCard padding="p-4" data-testid="scheduler-card">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg p-2 bg-[color:var(--copper-subtle)] text-[color:var(--copper-dark)]">
                                <Calendar size={18} />
                            </div>
                            <div>
                                <p className="font-body font-medium text-[color:var(--slate-deep)]">{pt.integrations.scheduler}</p>
                                <p className="font-mono text-[11px] text-[color:var(--slate-mid)]">
                                    {pt.integrations.schedulerNext}: {scheduler.jobs[0].next_run_time ? new Date(scheduler.jobs[0].next_run_time).toLocaleString("pt-PT", { dateStyle: "medium", timeStyle: "short" }) : "—"} (Africa/Luanda 07:00)
                                </p>
                            </div>
                        </div>
                        <NxButton variant="secondary" size="sm" onClick={runCron} disabled={runningCron} icon={<RefreshCw size={12} className={runningCron ? "animate-spin" : ""} />} data-testid="btn-run-cron">
                            {runningCron ? "A executar…" : pt.integrations.schedulerRunNow}
                        </NxButton>
                    </div>
                </GlassCard>
            )}

            {/* Connector grid */}
            <div>
                <p className="font-body text-xs uppercase tracking-[0.16em] text-[color:var(--slate-mid)] mb-3">{pt.integrations?.kpiTotal || "Conectores"}</p>
                {connectors === null ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-44" />)}</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="connectors-grid">
                        {connectors.map((c, idx) => {
                            const Icon = PROVIDER_ICONS[c.provider] || Plug;
                            const color = PROVIDER_COLOR[c.provider] || "#94A3B8";
                            return (
                                <motion.button
                                    key={c.id}
                                    type="button"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: Math.min(idx * 0.05, 0.3) }}
                                    onClick={() => setSelectedConnector(c)}
                                    className="text-left"
                                    data-testid={`connector-card-${c.id}`}
                                >
                                    <GlassCard hover padding="p-5" className="h-full">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="rounded-lg p-2.5" style={{ background: `${color}22`, color }}>
                                                <Icon size={20} strokeWidth={1.8} />
                                            </div>
                                            <NxBadge variant={STATUS_VARIANT[c.status]}>{pt.integrations.statuses[c.status]}</NxBadge>
                                        </div>
                                        <p className="font-display text-base text-[color:var(--slate-deep)] tracking-tight mb-1">{c.name}</p>
                                        <p className="font-body text-xs text-[color:var(--slate-mid)] line-clamp-2 mb-3 min-h-[32px]">{c.description}</p>
                                        <div className="flex items-center justify-between pt-3 border-t border-[color:var(--glass-border)]">
                                            <div>
                                                <p className="font-mono text-[10px] text-[color:var(--slate-mid)]">{c.total_events} {pt.integrations.events}</p>
                                                <p className="font-mono text-[10px] text-[color:var(--slate-mid)] flex items-center gap-1">
                                                    <Clock size={9} /> {_fmtRelative(c.last_sync_at)}
                                                </p>
                                            </div>
                                            <Sparkline data={c.sparkline_7d} color={color} />
                                        </div>
                                    </GlassCard>
                                </motion.button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Recent events */}
            <GlassCard padding="p-0" className="overflow-hidden" data-testid="recent-events">
                <div className="p-4 border-b border-[color:var(--glass-border)] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Activity size={14} className="text-[color:var(--copper)]" />
                        <h3 className="font-display text-base text-[color:var(--slate-deep)]">{pt.integrations.recentEvents}</h3>
                    </div>
                    <span className="font-mono text-xs text-[color:var(--slate-mid)]">{events.length} eventos</span>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                    {events.length === 0 ? (
                        <p className="p-6 text-center font-body text-sm text-[color:var(--slate-mid)]">{pt.integrations.empty}</p>
                    ) : (
                        events.map((e) => (
                            <button
                                key={e.id}
                                type="button"
                                onClick={() => setSelectedEvent(e)}
                                className="w-full text-left p-3 border-b border-[color:var(--glass-border)] hover:bg-[color:var(--copper-subtle)]/40 flex items-center gap-3"
                                data-testid={`event-row-${e.id}`}
                            >
                                <div className={"w-1.5 h-1.5 rounded-full " + (e.status === "success" ? "bg-[color:var(--success)]" : e.status === "failed" ? "bg-[color:var(--error)]" : "bg-[color:var(--warning)]")} />
                                <span className="font-mono text-[11px] text-[color:var(--copper-dark)]">{e.event_type}</span>
                                <span className="font-body text-xs text-[color:var(--slate-mid)] uppercase">{e.direction}</span>
                                {e.http_status && <span className="font-mono text-[10px] text-[color:var(--slate-mid)]">{e.http_status}</span>}
                                <span className="ml-auto font-mono text-[10px] text-[color:var(--slate-mid)]">{_fmtRelative(e.created_at)}</span>
                            </button>
                        ))
                    )}
                </div>
            </GlassCard>

            {/* Connector detail drawer */}
            {selectedConnector && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end lg:items-center justify-center p-2 lg:p-6" onClick={() => setSelectedConnector(null)}>
                    <motion.div
                        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[color:var(--bg-surface)] rounded-t-2xl lg:rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 border-t-2 lg:border-2 border-[color:var(--copper)]"
                        data-testid="connector-drawer"
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h2 className="font-display text-2xl text-[color:var(--slate-deep)]">{selectedConnector.name}</h2>
                                <div className="flex items-center gap-2 mt-2">
                                    <NxBadge variant={STATUS_VARIANT[selectedConnector.status]}>{pt.integrations.statuses[selectedConnector.status]}</NxBadge>
                                    <span className="font-mono text-xs text-[color:var(--slate-mid)]">{pt.integrations.types[selectedConnector.type]}</span>
                                </div>
                            </div>
                            <button onClick={() => setSelectedConnector(null)} className="text-[color:var(--slate-mid)]"><X size={18} /></button>
                        </div>
                        <p className="font-body text-sm text-[color:var(--slate-mid)] mb-4">{selectedConnector.description}</p>

                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="p-3 rounded-md bg-white/70 border border-[color:var(--glass-border)]">
                                <p className="font-mono text-[10px] uppercase text-[color:var(--slate-mid)]">Eventos</p>
                                <p className="font-mono text-lg text-[color:var(--copper-dark)]">{selectedConnector.total_events}</p>
                            </div>
                            <div className="p-3 rounded-md bg-white/70 border border-[color:var(--glass-border)]">
                                <p className="font-mono text-[10px] uppercase text-[color:var(--slate-mid)]">Taxa de Erro</p>
                                <p className="font-mono text-lg text-[color:var(--slate-deep)]">{selectedConnector.error_rate_pct}%</p>
                            </div>
                            <div className="p-3 rounded-md bg-white/70 border border-[color:var(--glass-border)]">
                                <p className="font-mono text-[10px] uppercase text-[color:var(--slate-mid)]">{pt.common.lastSync}</p>
                                <p className="font-mono text-xs text-[color:var(--slate-deep)]">{_fmtRelative(selectedConnector.last_sync_at)}</p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}

            {/* Event inspector */}
            {selectedEvent && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end lg:items-center justify-center p-2 lg:p-6" onClick={() => setSelectedEvent(null)}>
                    <motion.div
                        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[color:var(--bg-surface)] rounded-t-2xl lg:rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 border-t-2 lg:border-2 border-[color:var(--copper)]"
                        data-testid="event-inspector"
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--copper-dark)]">{pt.integrations.eventInspector}</p>
                                <h2 className="font-display text-xl text-[color:var(--slate-deep)] mt-1">{selectedEvent.event_type}</h2>
                                <div className="flex items-center gap-2 mt-2">
                                    <NxBadge variant={selectedEvent.status === "success" ? "success" : selectedEvent.status === "failed" ? "error" : "warning"}>{selectedEvent.status}</NxBadge>
                                    <span className="font-mono text-xs text-[color:var(--slate-mid)]">{selectedEvent.direction}</span>
                                    {selectedEvent.http_status && <span className="font-mono text-xs text-[color:var(--slate-mid)]">HTTP {selectedEvent.http_status}</span>}
                                </div>
                            </div>
                            <button onClick={() => setSelectedEvent(null)} className="text-[color:var(--slate-mid)]"><X size={18} /></button>
                        </div>
                        {selectedEvent.error_message && (
                            <div className="p-3 rounded-md bg-red-50/60 border border-[color:var(--error)] mb-3" data-testid="event-error">
                                <p className="font-body text-sm text-[color:var(--error)]">{selectedEvent.error_message}</p>
                            </div>
                        )}
                        <p className="font-mono text-[10px] uppercase text-[color:var(--slate-mid)] mb-1">Payload</p>
                        <pre className="bg-[color:var(--slate-deep)] text-[color:var(--copper-light)] p-4 rounded-md font-mono text-[11px] overflow-x-auto leading-relaxed" data-testid="event-payload">
{JSON.stringify(selectedEvent.payload, null, 2)}
                        </pre>
                        {selectedEvent.status === "failed" && (
                            <NxButton variant="copper" size="sm" onClick={() => retryEvent(selectedEvent.id)} icon={<RefreshCw size={13} />} className="mt-3" data-testid="btn-retry-event">
                                {pt.integrations.retry}
                            </NxButton>
                        )}
                    </motion.div>
                </motion.div>
            )}

            <AnimatePresence>
                {showWizard && <Wizard onClose={() => setShowWizard(false)} onCreated={load} />}
            </AnimatePresence>
        </div>
    );
};

export default IntegrationsPage;
